import React, { useMemo, useState, useEffect } from 'react';
import {
  Text,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Anchor,
  Center,
  Group,
  Stack,
  Switch,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import SQLEditorPopup from '../SQLEditorPopup';
import MembershipPopup from '../MembershipPopup';

function PurchasingIndividualNewVendorPODetails() {
  const { ccode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });

  // State for pop-up dialogs for query links
  const [openNewVendorSummary, setOpenNewVendorSummary] = useState(false);
  const [openNewVendorPODetails, setOpenNewVendorPODetails] = useState(false);
  const [openMembership, setOpenMembership] = useState(false);

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  // Clear handlers for proper dialog closing
  const handleCloseMembership = () => setOpenMembership(false);
  const handleCloseNewVendorSummary = () => setOpenNewVendorSummary(false);
  const handleCloseNewVendorPODetails = () => setOpenNewVendorPODetails(false);

  // Construct the REST endpoint URL with ccode, period, and filter parameters
  const url = `${config.apiBaseUrl}/supply-chain/purchasing/individual/scorecard/new-vendor-po-details?ccode=${ccode}&period=${period}&filter=${warehouseFilter}`;
  const { data, isLoading, error } = useAuthenticatedData(url);
  const { data: purchaserName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  // Handle tab changes for period selection
  const handleTabChange = (newPeriod) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('period', newPeriod);
    newSearchParams.set('filter', warehouseFilter);
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  // Process the REST data into rows for the table.
  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index,
      ...item.values,
    }));
  }, [data]);

  // Generate export filename: e.g. Purchasing-John-Smith-mtd-new-vendor-po-details
  const generateExportFileName = () => {
    return `Purchasing-${purchaserName}-${period}-new-vendor-po-details`;
  };

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Vendor': row.name || '',
      'Buyer': row.fullname || '',
      'Doc No': row.docno || '',
      'User Line': row.userline || '',
      'Added On': row.addedondate ? new Date(row.addedondate).toLocaleDateString() : '',
      'Part Number': row.partnumber || '',
      'Spend': row.spend || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  // Define table columns based on the sample data structure.
  const columns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Vendor', size: 150 },
      { accessorKey: 'fullname', header: 'Buyer', size: 150 },
      { accessorKey: 'docno', header: 'Doc No', size: 120 },
      { accessorKey: 'userline', header: 'User Line', size: 80 },
      {
        accessorKey: 'addedondate',
        header: 'Added On',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      {
        accessorKey: 'spend',
        header: 'Spend',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  if (isLoading || isLoadingName) return <Center><Loader /></Center>;
  if (error) return <Alert color="red" title="Error">{error.message}</Alert>;
  if (errorName) return <Alert color="red" title="Error">{errorName.message}</Alert>;

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header Section with Title and Filter Switch */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Individual Scorecard - New Vendor PO Details - {purchaserName}
        </Text>
        
        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* Technical options: small links placed under the title */}
      <Group gap="sm" mb="sm">
         <Anchor
                 size="xs"
                 c="dimmed"
                 onClick={() =>
                   window.open(
                     '/query-commander/editor?queryKey=purchasing_individual_new_vendor_growth_summary',
                     '_blank'
                   )
                 }
                 style={{ cursor: 'pointer' }}
               >
                 Summary Query
               </Anchor>
               <Anchor
                 size="xs"
                 c="dimmed"
                 onClick={() =>
                   window.open(
                     '/query-commander/editor?queryKey=scorecards-supply-chain-purchasing-individual-scorecard-new-vendor-base-growth-po-details',
                     '_blank'
                   )
                 }
                 style={{ cursor: 'pointer' }}
               >
                 Details Query
               </Anchor>
      </Group>

      {/* About the Data Block */}
      <Box mb="sm">
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Text size="sm" fw={500} mb={4}>
            About the Data
          </Text>
          <Text size="xs" c="dimmed">
            These are vendors that {purchaserName} has bought from (by creating a purchase order) 
            that we didn't buy from last year. When {purchaserName} buys from one of these vendors, we count them as 
            "new". These are the purchase orders for those vendors.
            Currently you're seeing data {period}.
          </Text>
        </Card>
      </Box>

      {/* Tabs for period selection */}
      <Tabs value={period} onChange={handleTabChange} mb="sm">
        <Tabs.List>
          <Tabs.Tab value="MTD">Month-to-Date</Tabs.Tab>
          <Tabs.Tab value="QTD">Quarter-to-Date</Tabs.Tab>
          <Tabs.Tab value="YTD">Year-to-Date</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Paper shadow="sm" p="sm" style={{ height: 400 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>

      
    </Paper>
  );
}

export default PurchasingIndividualNewVendorPODetails;